import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import { Typography, Theme } from '@mui/material';
import { Colors } from '../../../../theme/colors';
import { toastFailure, toastSuccess } from '../../../../utils/toast';
import { Campaigns } from '../../../../modals/modals';
import { useDrop } from 'react-dnd';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchQuestions,
  clearQuestion,
} from '../../../../redux/questions/question-slice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './styles.css';
import { createAIJobDescription } from '../../../../api/questions';
import { DrawerRightNew } from '../../../../components/dashboard/DrawerRightNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchAllEnterprises } from '../../../../redux/enterprise/enterprise-slice';
import { useSelector } from 'react-redux';
import 'react-phone-number-input/style.css';
import { getUserByIDSlice } from '../../../../redux/user/user-slice';
import { getPaymentDetailsByUser } from '../../../../redux/payment/payments-slice';

interface LocationPropsState {
  item: Campaigns;
}
interface ICreateCampaignProps {
  pageType: string;
  campaignJobTitle: string;
  onUpdateJobDescription: (jd: string, html: string) => void;
  contentHtml: string;
  content: string
}

const useStylesCss = makeStyles((theme: Theme) => ({
  root: {
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '12px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '2px',
      backgroundColor: Colors.Maximum_blue_purple,
    },
  },
}));

const JobDescriptionForm: React.FC<ICreateCampaignProps> = ({
  pageType,
  campaignJobTitle,
  onUpdateJobDescription,
  content,
  contentHtml
}) => {
  const classes = useStylesCss();
  const location = useLocation();
  const navigate = useNavigate();
  const quillRef = useRef<ReactQuill | null>(null);
  const state = location.state as LocationPropsState;

  const candidateRef = useRef<HTMLDivElement>(null);
  const AiRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  // ----------------- Local State -----------------
  const [campaignDesc, setCampaignDesc] = useState('');
  const [campaignDescHtml, setCampaignDescHtml] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [storeAIResponse, setStoreAIResponse] = useState<any[] | null>(null);
  const [isAIApiCallJD, setIsAIApiCallJD] = useState(false);

  // ----------------- Redux State -----------------
  const isSideBarOpenRight = useSelector((state: any) => state.dashboard.isSideBarOpenRight);
  const { jobdescriptionAPICall, jobDescriptionHtml } = useAppSelector((state) => state.questions);
  const { jobTemplates } = useAppSelector((state) => state.jobtemplates);
  const { enterprises, enterpris } = useAppSelector((state) => state.enterprises);

  // ----------------- Effects -----------------
  useEffect(() => {
    if (campaignJobTitle) {
      generateJobDescription();
    }
  }, [campaignJobTitle]);

  const generateJobDescription = useCallback(() => {
    setIsAIApiCallJD(true);
    createAIJobDescription(campaignJobTitle)
      .then((res) => {
        if (res?.data) setStoreAIResponse(res.data);
      })
      .catch(() => toastFailure('Failed to generate AI job description.'))
      .finally(() => setIsAIApiCallJD(false));
  }, [campaignJobTitle]);

  useEffect(() => {
    // Handle browser back/forward navigation
    const handlePopstate = () => navigate('/campaign');
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [navigate]);

  useEffect(() => {
    if (jobDescriptionHtml && !jobdescriptionAPICall) {
      setStoreAIResponse(jobDescriptionHtml);
    }
  }, [jobDescriptionHtml, jobdescriptionAPICall]);

  useEffect(() => {
    if (pageType === 'create' && !content && !contentHtml) {
      if (storeAIResponse) handleInsertJD(storeAIResponse);
    } else {
      setCampaignDescHtml(contentHtml);
      setCampaignDesc(content);
      // handleInsertJD(content)
    }
  }, [storeAIResponse]);

  useEffect(() => {
    if (pageType !== 'create') {
      dispatch(fetchAllEnterprises());
    }
  }, [dispatch, pageType]);

  useEffect(() => {
    if (state && enterpris) {
      dispatch(getPaymentDetailsByUser({
        userId: enterpris?.fk_User_ID,
        enterpriseId: state?.item?.fk_Enterprise_ID,
      }));
    }
  }, [dispatch, state, enterpris]);

  useEffect(() => {
    if (enterpris) {
      dispatch(getUserByIDSlice(enterpris.fk_UserID_MainContact));
    }
  }, [dispatch, enterpris]);

  useEffect(() => {
    if (enterprises?.length && state) {
      const company = enterprises.find(
        (itm) => itm.pk_Enterprise_ID === state.item.fk_Enterprise_ID
      );
      if (company) setSelectedCompany(company);
    }
  }, [enterprises, state]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (candidateRef.current && !candidateRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mouseup', handleClickOutside);
    return () => document.removeEventListener('mouseup', handleClickOutside);
  }, []);

  useEffect(() => {
    if (state) {
      if (pageType !== 'create') dispatch(fetchQuestions(state.item.pk_Campaign_ID));
    } else {
      dispatch(clearQuestion());
    }
  }, [dispatch, state, pageType]);

  // ----------------- Handlers -----------------
  const handleInsertJD = useCallback((data: string[]) => {
    const newHtml = campaignDescHtml + data.join('<br /> ');
    const newText = campaignDesc + data.join(' ');

    setCampaignDescHtml(newHtml);
    setCampaignDesc(newText);

    onUpdateJobDescription(newText, newHtml);
  }, [campaignDesc, campaignDescHtml, onUpdateJobDescription]);

  const copyToClipboard = useCallback(() => {
    if (campaignDesc) {
      navigator.clipboard.writeText(campaignDesc)
        .then(() => toastSuccess('Successfully copied.'))
        .catch(() => toastFailure('Failed to copy.'));
    }
  }, [campaignDesc]);

  // ----------------- Drag & Drop -----------------
  const [, dropJob] = useDrop(
    () => ({
      accept: ['jobItem'],
      drop: (item: any) => {
        if (!state) return toastFailure('Job Title is required.');
        if (!quillRef.current) return;

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        if (item.data[0]) {
          const textToInsert = item.data[0].Job_Description;

          if (range) {
            editor.deleteText(range.index, range.length);
            editor.clipboard.dangerouslyPasteHTML(range.index, textToInsert);
            editor.setSelection(range.index + textToInsert.length, 0);
          } else {
            editor.clipboard.dangerouslyPasteHTML(editor.getLength() - 1, textToInsert);
          }

          // update state
          const newHtml = editor.root.innerHTML;
          const newText = editor.getText();
          setCampaignDescHtml(newHtml);
          setCampaignDesc(newText);
          onUpdateJobDescription(newText, newHtml);
        }
      },
    }),
    [state, onUpdateJobDescription]
  );


  // ----------------- Quill Config -----------------
  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['clean'],
    ],
  }), []);

  const formats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'list', 'bullet', 'indent', 'link', 'image',
  ], []);

  // ----------------- Render -----------------
  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        flexDirection: 'row',
        width: '100%',
        overflowX: 'hidden',
        height: 'calc(100vh - 250px)',
      }}
    >
      {/* Left Panel */}
      <Box
        className={classes.root}
        sx={{
          width: '60%',
          height: 'calc(100vh - 250px)',
          position: 'relative',
          overflowY: 'scroll',
          transition: 'width 0.1s ease-in-out',
          paddingRight: '22px',
        }}
      >
        {(isSideBarOpenRight || pageType === 'create') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
            {/* Drop Zone */}
            <Box
              sx={{
                backgroundColor: Colors.white,
                display: 'flex',
                justifyContent: 'center',
                height: 80,
                borderRadius: 2,
                alignItems: 'center',
                color: Colors.gray1,
                border: `1px dotted ${Colors.gray_A9}`,
                mt: 1,
              }}
              ref={dropJob}
            >
              <Typography fontSize="12px" fontWeight={600} color={Colors.gray_97}>
                Drop job description from templates here
              </Typography>
            </Box>

            {/* Editor */}
            <Box sx={{ position: 'relative', mt: 2 }}>
              <ContentCopyIcon
                sx={{
                  position: 'absolute',
                  right: 11,
                  top: 11,
                  fontSize: 20,
                  cursor: 'pointer',
                  color: Colors.primary,
                }}
                onClick={copyToClipboard}
              />
              <ReactQuill
                ref={quillRef}
                readOnly={false}
                modules={modules}
                formats={formats}
                value={campaignDescHtml}
                className="job-title"
                placeholder="Enter the job description here"
                onChange={(content, _, __, editor) => {
                  const text = editor.getText(0);
                  setCampaignDescHtml(content);
                  setCampaignDesc(text);
                  onUpdateJobDescription(text, content);
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      <Box
        className={classes.root}
        sx={{
          display: 'flex',
          width: '40%',
          transition: 'width 0.1s ease-in-out',
          px: pageType === 'edit' ? '22px' : 0,
          mt: pageType === 'edit' ? '110px' : 0,
          height: pageType === 'create' ? 'calc(100vh - 250px)' : 'calc(100vh - 210px)',
          background: pageType === 'edit' ? Colors.white : 'transparent',
        }}
      >
        <DrawerRightNew
          isCreateCampaign
          selectedCompany={selectedCompany}
          stateData={state}
          pageType={pageType}
          isQuestionTemplate={false}
          labelColor={Colors.black}
          isInstantJob
        />
      </Box>
    </Box>
  );
};

export default JobDescriptionForm;
